import { useState } from 'react';
import { toast } from 'sonner';
import { useFeedbackList, useFeedbackPatterns, useDeleteFeedback } from '../../api/feedback';
import { useAuth } from '../../hooks/useAuth';
import PageHeader from '../../components/layout/PageHeader';
import { MessageSquare, BarChart2 } from 'lucide-react';
import { TablePageSkeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import ErrorState from '../../components/ui/ErrorState';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatDate, weekLabel } from '../../lib/dates';

type Tab = 'entries' | 'patterns';

export default function FeedbackInboxPage() {
  const [tab, setTab] = useState<Tab>('entries');
  const { currentUser } = useAuth();

  const { data: entries,  isLoading: loadingEntries,  error: errorEntries,  refetch: refetchEntries  } = useFeedbackList();
  const { data: patterns, isLoading: loadingPatterns, error: errorPatterns, refetch: refetchPatterns } = useFeedbackPatterns();
  const deleteFeedback = useDeleteFeedback();

  return (
    <div className="max-w-2xl">
      <PageHeader title="Feedback inbox" />

      {/* Tabs */}
      <div className="mb-5 flex border-b border-border">
        {(['entries', 'patterns'] as Tab[]).map((t) => (
          <button
            key={t}
            className={cn(
              'px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px transition-colors',
              tab === t
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'entries' && (
        <>
          {loadingEntries && <TablePageSkeleton cols={4} hasAction={false} />}
          {errorEntries   && <ErrorState onRetry={refetchEntries} />}
          {!loadingEntries && !errorEntries && (
            entries?.length === 0
              ? <EmptyState icon={MessageSquare} title="No feedback yet" description="Submitted feedback from the team will appear here." />
              : (
                <div className="space-y-3">
                  {entries?.map((f) => (
                    <Card key={f.id} className="overflow-hidden">
                      <div className="flex items-center gap-2 border-b border-border px-5 py-3">
                        <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                          {weekLabel(f.weekOf)}
                        </span>
                        <span className="text-xs text-muted-foreground">· submitted {formatDate(f.createdAt)}</span>
                        {currentUser?.id === f.engineerId && (
                          <button
                            onClick={() => deleteFeedback.mutate(f.id, { onSuccess: () => toast.success('Feedback deleted.') })}
                            disabled={deleteFeedback.isPending}
                            className="ml-auto text-xs text-destructive hover:underline disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <div className="px-5 py-4">
                        <p className="whitespace-pre-wrap text-sm text-foreground">{f.text}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              )
          )}
        </>
      )}

      {tab === 'patterns' && (
        <>
          {loadingPatterns && <TablePageSkeleton cols={3} hasAction={false} />}
          {errorPatterns   && <ErrorState onRetry={refetchPatterns} />}
          {!loadingPatterns && !errorPatterns && (
            <>
              <p className="mb-4 text-xs text-muted-foreground">
                Weeks with fewer than 3 distinct sources are excluded to protect individual anonymity.
              </p>
              {patterns?.length === 0
                ? <EmptyState icon={BarChart2} title="No patterns yet" description="Minimum 3 distinct sources required per week." />
                : (
                  <Card>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Week</TableHead>
                          <TableHead className="text-right">Responses</TableHead>
                          <TableHead className="text-right">Sources</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {patterns?.map((p) => (
                          <TableRow key={p.weekOf}>
                            <TableCell className="text-foreground">{weekLabel(p.weekOf)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{p.totalResponses}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{p.distinctSources}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                )
              }
            </>
          )}
        </>
      )}
    </div>
  );
}
